import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { EquipmentWhereInput } from '@api/equipments/equipments.type';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { TreeViewService } from '@core/components/fc-tree-view/fc-tree-view.service';
import { TreeViewItem } from '@core/components/fc-tree-view/fc-tree.types';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { Equipment } from '@core/interfaces';
import { LocationEnvironment } from '@core/interfaces/locations-environments';
import { LocationSector } from '@core/interfaces/locations-sectors';
import { delay } from '@core/utils/delay';
import { LocationTabLinksService } from 'app/main/locations/dialogs/dialog-location/tabs/links/tab-links.service';
import { uniqBy } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { TabLinksService } from '../tab-links.service';
import { LinkSectorsEnvironmentsEquipmentsParams } from '../tab-links.types';
import { DialogLinkService } from './dialog-link.service';
import { DialogLinkData } from './dialog-link.types';

@Component({
  selector: 'dialog-link',
  templateUrl: './dialog-link.component.html',
  styleUrls: ['./dialog-link.component.scss']
})
export class DialogLinkComponent implements OnInit, OnDestroy {
  public isEmpty = false;
  public isLoading = true;

  public searchControl = new FormControl();

  public equipments: Equipment[] = [];
  public locationSectors: LocationSector[] = [];
  public locationEnvironments: LocationEnvironment[] = [];

  public allEquipments: Equipment[] = [];
  public allLocationSectors: LocationSector[] = [];
  public allLocationEnvironments: LocationEnvironment[] = [];

  public locationLinks: TreeViewItem[] = [];
  public unlinkedEquipments: TreeViewItem[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogLinkData,
    private readonly tabLinksService: TabLinksService,
    private readonly treeViewService: TreeViewService,
    private readonly dialogLinkService: DialogLinkService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly messageService: CustomMessagesService,
    private readonly matDialogRef: MatDialogRef<DialogLinkComponent>,
    private readonly locationLinksTabModalService: LocationTabLinksService,
    private readonly authenticationService: AuthenticationService
  ) {
    this.dialogLinkService.linkedSectors = this.dialogData.linkedSectors;
    this.dialogLinkService.linkedEquipments = this.dialogData.linkedEquipments;
    this.dialogLinkService.linkedEnvironments = this.dialogData.linkedEnvironments;
  }

  public async ngOnInit(): Promise<void> {
    await this.loadLocationLinks();

    this.searchControl.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(async () => {
        const { sectors, environments, equipments } = this.dialogLinkService.getLinkedItems({
          locationLinks: this.locationLinks,
          unlinkedEquipments: this.unlinkedEquipments,
          allSectors: this.allLocationSectors,
          allEquipments: this.allEquipments,
          allEnvironments: this.allLocationEnvironments,
        });

        this.dialogLinkService.linkedSectors = sectors;
        this.dialogLinkService.linkedEquipments = equipments;
        this.dialogLinkService.linkedEnvironments = environments;

        await this.loadLocationLinks();
      });

    this.treeViewService.checked$
      .pipe(takeUntil(this.destroy$))
      .subscribe((item) => {
        if (item.metadata === 'sector') {
          const linkedSector = this.dialogLinkService.linkedSectors.find((linkedItem) => linkedItem.id === item.id);
          if (!linkedSector) {
            if (item.action.checked) {
              this.dialogLinkService.linkedSectors.push(item.original);
            }

            return;
          }

          if (!item.action.checked) {
            this.dialogLinkService.linkedSectors = this.dialogLinkService.linkedSectors.filter((linkedItem) => linkedItem.id !== item.id);
            this.dialogLinkService.linkedEnvironments = this.dialogLinkService.linkedEnvironments.filter((linkedItem) => linkedItem.locationSector.id !== item.id);
            this.dialogLinkService.linkedEquipments = this.dialogLinkService.linkedEquipments.filter((linkedItem) => !linkedItem.locationSector || linkedItem.locationSector.id !== item.id);
          }
        }

        if (item.metadata === 'environment') {
          const linkedEnvironment = this.dialogLinkService.linkedEnvironments.find((linkedItem) => linkedItem.id === item.id);
          if (!linkedEnvironment) {
            if (item.action.checked) {
              this.dialogLinkService.linkedEnvironments.push(item.original);
            }

            return;
          }

          if (!item.action.checked) {
            this.dialogLinkService.linkedEnvironments = this.dialogLinkService.linkedEnvironments.filter((linkedItem) => linkedItem.id !== item.id);
            this.dialogLinkService.linkedEquipments = this.dialogLinkService.linkedEquipments.filter((linkedItem) => !linkedItem.locationEnvironment || linkedItem.locationEnvironment.id !== item.id);
          }
        }

        if (item.metadata === 'equipment') {
          const linkedEquipment = this.dialogLinkService.linkedEquipments.find((linkedItem) => linkedItem.id === item.id);
          if (!linkedEquipment) {
            if (item.action.checked) {
              this.dialogLinkService.linkedEquipments.push(item.original);
            }

            return;
          }

          if (!item.action.checked) {
            this.dialogLinkService.linkedEquipments = this.dialogLinkService.linkedEquipments.filter((linkedItem) => linkedItem.id !== item.id);
          }
        }
      });

    this.treeViewService.checkAll$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.locationLinks.forEach((sector) => {
          sector.action.checked = true;

          if (sector.children && sector.children.length) {
            sector.children.forEach((environmentOrEquipment) => {
              environmentOrEquipment.action.checked = true;

              if (environmentOrEquipment.children && environmentOrEquipment.children.length) {
                environmentOrEquipment.children.forEach((equipment) => {
                  equipment.action.checked = true;
                });
              }
            });
          }
        });

        this.unlinkedEquipments.forEach((equipment) => {
          equipment.action.checked = true;
          equipment.children.forEach((eq) => {
            eq.action.checked = true;
          });
        });
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public checkAll(): void {
    this.treeViewService.checkAll$.next();
    this.changeDetectorRef.markForCheck();
  }

  public save(): void {
    const result = this.dialogLinkService.getLinkedItems({
      locationLinks: this.locationLinks,
      unlinkedEquipments: this.unlinkedEquipments,
      allSectors: this.allLocationSectors,
      allEquipments: this.allEquipments,
      allEnvironments: this.allLocationEnvironments,
    });

    this.matDialogRef.close(result);
  }

  private async loadLocationLinks(): Promise<void> {
    this.isLoading = true;

    const response = await this.searchLocationSectorsWithEnvironmentsAndEquipments().catch((error) => {
      this.messageService.error('UNKNOWN_ERROR');
      console.error( error);
      sentry.captureException(error);
      return [];
    });
    const unlinkedEquipments = await this.searchEquipments().catch((error) => {
      this.messageService.error('UNKNOWN_ERROR');
      console.error( error);
      sentry.captureException(error);
      return [];
    });

    await this.populateLocationLinks({
      locationSectors: response,
      unlinkedEquipments: unlinkedEquipments
    });

    this.isLoading = false;
  }

  private async searchLocationSectorsWithEnvironmentsAndEquipments(): Promise<LocationSector[]> {
    return this.locationLinksTabModalService.searchLocationSectorsWithEnvironmentsAndEquipments({
      locationId: this.dialogData.locationId,
      archived: false,
      nameOrEnvironmentNameContains: this.searchControl.value,
    });
  }

  private async searchEquipments(): Promise<Equipment[]> {
    const params: EquipmentWhereInput = {
      locationId_in: [this.dialogData.locationId],
      segmentId_in: [this.dialogData.segmentId],
      name_or_number_contains: this.searchControl.value,
      archived: false,
      hasLocationEnvironment: false,
      hasLocationSector: false
    };

    return this.locationLinksTabModalService.searchEquipmentsWithWarranties(params);
  }

  private async populateLocationLinks(params: {
    locationSectors: LocationSector[],
    unlinkedEquipments: Equipment[]
  }): Promise<void> {
    const locationSectors = params.locationSectors;
    const locationEnvironments = locationSectors.map((sector) => sector.locationEnvironments || []).reduce((acc, curr) => acc.concat(curr), []);
    const equipmentsWithoutEnvironment = locationSectors.map((env) => env.equipmentsWithoutEnvironment || []).reduce((acc, curr) => acc.concat(curr), []);
    const equipmentsWithEnvironment = locationEnvironments.map((env) => env.equipments || []) || [].reduce((acc, curr) => acc.concat(curr), []);
    const equipments = [...equipmentsWithEnvironment, ...equipmentsWithoutEnvironment, ...params.unlinkedEquipments];

    if (!this.searchControl.value) {
      this.allEquipments = equipments;
      this.allLocationSectors = locationSectors;
      this.allLocationEnvironments = locationEnvironments;
    }

    this.equipments = equipments;
    this.locationSectors = locationSectors;
    this.locationEnvironments = locationEnvironments;

    const concatLocationSectors = [...(this.locationSectors.concat(this.allLocationSectors))];
    const concatLocationEnvironments = [...(this.locationEnvironments.concat(this.allLocationEnvironments))];
    const concatEquipments = [...(this.equipments.concat(this.allEquipments))];

    const uniqueSectors = uniqBy(concatLocationSectors || [], 'id');
    const uniqueEnvironments = uniqBy(concatLocationEnvironments || [], 'id');
    const uniqueEquipments = uniqBy(concatEquipments || [], 'id');

    const data: LinkSectorsEnvironmentsEquipmentsParams = {
      sectors: uniqueSectors,
      environments: uniqueEnvironments,
      equipments: uniqueEquipments,
    };

    const { sectors, unlinkedEquipments } = this.tabLinksService.sortAllByArchivedThenByName(uniqueSectors, params.unlinkedEquipments);
    const unfilteredLocationLinks = this.dialogLinkService.formatSectors({ sectors, data });
    const unfilteredUnlinkedEquipments = this.dialogLinkService.formatUnlinkedEquipments({ unlinkedEquipments });
    this.locationLinks = this.tabLinksService.filter(unfilteredLocationLinks, this.searchControl.value);
    this.unlinkedEquipments = this.tabLinksService.filter(unfilteredUnlinkedEquipments, this.searchControl.value);

    this.isEmpty = !this.locationLinks.length && !this.unlinkedEquipments.length;
  }
}
